import { FC, useCallback, useState } from "react";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { EntityProfileType } from "@common/types/entity";
import { HStack } from "@design-system/HStack/HStack";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { useFormikContext } from "formik";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useConnectQuickbooks } from "@hooks/useConnectQuickbooks";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { trackEvent } from "@lib/analytics";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import {
    ExternalSystemGrid,
    SUPPORTED_MIGRATION_SYSTEMS,
} from "@components/accountingMigration/source";
import { ClientFormSchema } from "../../AddClientSteps/lib";
import containerStyles from "../../AddClientDrawerStepsContainer.module.scss";
import styles from "../AddClientDrawerSteps.module.scss";
import { CoaSetupTiles } from "./CoaSetupTiles";
import { CoaSourceFlow } from "./CoaSourceFlow";
import { CoaEntitySourceSelect } from "./CoaEntitySourceSelect";
import { MigrationSetupTile } from "./MigrationSetupTile";
import {
    OtherSystemView,
    UnsupportedSystemView,
} from "./ExternalSystemSelectedViews";
import { getCoaSetupTileVisibility } from "./lib";
import { QboMigrationMethodView } from "./QboMigrationMethodView";
import { useCopySourceEntities } from "./useCopySourceEntities";
import { useNewClientCoaTemplates } from "./useNewClientCoaTemplates";

interface Props {
    onNext: () => void;
    qboConnection?: IntegrationConnection | null;
    setQboConnection: (connection: IntegrationConnection | null) => void;
    workspaceId?: string;
}

export const SetupMethodStep: FC<Props> = ({
    onNext,
    qboConnection,
    setQboConnection,
    workspaceId,
}) => {
    const { setFieldValue, values } = useFormikContext<ClientFormSchema>();
    const { qboFileMigration } = useTypedFlags();
    const [isQboMethodView, setIsQboMethodView] = useState(false);
    const [isMigrationExpanded, setIsMigrationExpanded] = useState(false);

    const { templates, isFetched: areTemplatesFetched } =
        useNewClientCoaTemplates({ workspaceId });

    const hasTemplates = (templates?.length ?? 0) > 0;

    const {
        entities: copySourceEntities,
        canEditCoa,
        isOrganizationWorkspace,
        areCopySourcesFetched,
    } = useCopySourceEntities({ workspaceId });
    const { isTemplateTileVisible, isEntityTileVisible } =
        getCoaSetupTileVisibility({
            mode: values.mode,
            isOrganizationWorkspace,
            canEditCoa,
            hasCopySourceEntities: copySourceEntities.length > 0,
            areCopySourcesFetched,
        });

    const { connect: connectQBO, isPending: connectingQBO } =
        useConnectQuickbooks({
            workspaceId,
            onSuccess: (connection) => {
                setQboConnection(connection);
                setFieldValue("client.businessName", connection.label);
            },
        });
    const applyExternalSystem = useCallback(
        (setupMethod: AccountingMigrationExternalSystem | null) => {
            if (connectingQBO) {
                return;
            }

            setFieldValue("migrationMethod", setupMethod);
            setFieldValue("syncChartOfAccounts", true);
            setFieldValue("shouldUseExternalChartOfAccounts", true);
            setFieldValue("coaSetupMethod", "kick");
            setFieldValue("coaTemplateId", null);
            setFieldValue("coaSourceEntityId", null);
            setFieldValue(
                "client.legalType",
                EntityProfileType.SOLE_PROPRIETORSHIP,
            );

            if (!SUPPORTED_MIGRATION_SYSTEMS.includes(setupMethod)) {
                void trackEvent(
                    AnalyticsEvent.ACCOUNTING_MIGRATION_UNSUPPORTED_SOFTWARE_SELECTED,
                    { externalSystem: setupMethod ?? "other", workspaceId },
                );
            }

            if (
                setupMethod ===
                AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE
            ) {
                connectQBO();
            }
        },
        [setFieldValue, connectingQBO, connectQBO, workspaceId],
    );

    const selectExternalSystem = useCallback(
        (setupMethod: AccountingMigrationExternalSystem | null) => {
            const shouldChooseQboMethod =
                qboFileMigration &&
                setupMethod ===
                    AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;
            setIsQboMethodView(shouldChooseQboMethod);

            if (!shouldChooseQboMethod) {
                applyExternalSystem(setupMethod);
            }
        },
        [qboFileMigration, applyExternalSystem],
    );

    const deselectExternalSystem = useCallback(() => {
        setFieldValue("migrationMethod", null);
        setFieldValue("syncChartOfAccounts", false);
        setFieldValue("shouldUseExternalChartOfAccounts", false);
    }, [setFieldValue]);

    const isSystemDifferentThanQBO =
        !!values.migrationMethod &&
        values.migrationMethod !==
            AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;
    const isUnsupportedSystem =
        values.syncChartOfAccounts &&
        !SUPPORTED_MIGRATION_SYSTEMS.includes(values.migrationMethod);
    const isNonKickSetupMethod = values.coaSetupMethod !== "kick";
    const isTemplateSetupMethod = values.coaSetupMethod === "template";

    const selectKick = useCallback(() => {
        setFieldValue("syncChartOfAccounts", false);
        setFieldValue("shouldUseExternalChartOfAccounts", false);
        setFieldValue("migrationMethod", null);
        setFieldValue("coaSetupMethod", "kick");
        setFieldValue("coaTemplateId", null);
        setFieldValue("coaSourceEntityId", null);
        setFieldValue(
            "client.legalType",
            EntityProfileType.SOLE_PROPRIETORSHIP,
        );

        onNext();
    }, [setFieldValue, onNext]);

    const selectTemplateTile = useCallback(() => {
        setFieldValue("migrationMethod", null);
        setFieldValue("syncChartOfAccounts", false);
        setFieldValue("shouldUseExternalChartOfAccounts", false);
        setFieldValue("coaSetupMethod", "template");
    }, [setFieldValue]);

    const selectEntityTile = useCallback(() => {
        setFieldValue("migrationMethod", null);
        setFieldValue("syncChartOfAccounts", false);
        setFieldValue("shouldUseExternalChartOfAccounts", false);
        setFieldValue("coaSetupMethod", "entity");
    }, [setFieldValue]);

    useKeyboardCommand({
        keys: "enter",
        callback: selectKick,
        options: {
            enabled:
                !connectingQBO &&
                !isUnsupportedSystem &&
                !isSystemDifferentThanQBO &&
                !isNonKickSetupMethod &&
                !isQboMethodView &&
                !isMigrationExpanded,
            enableOnFormTags: true,
            description: "Select Kick Chart of Accounts",
        },
    });

    if (connectingQBO || qboConnection) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    {connectingQBO
                        ? "Connecting to QuickBooks…"
                        : "QuickBooks connected"}
                </div>
                <div className={styles.subheader}>
                    You'll finish setting up the Chart of Accounts after
                    onboarding.
                </div>

                <div className={containerStyles.footer}>
                    <div />

                    {qboConnection && (
                        <WithHotkey
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: "Next",
                                enabled: true,
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    type="button"
                                    onClick={() => onNext()}
                                    size="xl"
                                    rightIcon={hotkeyHint}
                                >
                                    Next
                                </Button>
                            )}
                        </WithHotkey>
                    )}
                </div>
            </div>
        );
    }

    if (isUnsupportedSystem) {
        return <UnsupportedSystemView onBack={deselectExternalSystem} />;
    }

    if (isSystemDifferentThanQBO) {
        return (
            <OtherSystemView
                migrationMethod={values.migrationMethod}
                onBack={deselectExternalSystem}
                onNext={onNext}
            />
        );
    }

    if (isQboMethodView) {
        return (
            <QboMigrationMethodView
                onConnect={() =>
                    applyExternalSystem(
                        AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE,
                    )
                }
                onUpload={() =>
                    applyExternalSystem(
                        AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE_GL_COA,
                    )
                }
                onBack={() => setIsQboMethodView(false)}
            />
        );
    }

    if (values.coaSetupMethod === "entity") {
        return (
            <CoaEntitySourceSelect
                entities={copySourceEntities}
                onNext={onNext}
            />
        );
    }

    if (isTemplateSetupMethod) {
        return <CoaSourceFlow onNext={onNext} workspaceId={workspaceId} />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                How would you like to set up your Chart of Accounts?
            </div>
            <div className={styles.methodOptionsContainer}>
                <div
                    className={styles.kickOption}
                    onClick={selectKick}
                    role="button"
                >
                    <div className={styles.kickOptionContent}>
                        <div>
                            <KickAvatar />
                        </div>
                        <div className={styles.kickOptionTexts}>
                            <div className={styles.kickOptionTitle}>
                                Use Kick Chart of Accounts
                            </div>
                            <div className={styles.kickOptionDescription}>
                                You can migrate later
                            </div>
                        </div>
                    </div>
                    <HStack>
                        <Shortcut keys="enter" />
                    </HStack>
                </div>
                <CoaSetupTiles
                    isTemplateTileVisible={isTemplateTileVisible}
                    areTemplatesFetched={areTemplatesFetched}
                    hasTemplates={hasTemplates}
                    isEntityTileVisible={isEntityTileVisible}
                    areCopySourcesFetched={areCopySourcesFetched}
                    onSelectTemplate={selectTemplateTile}
                    onSelectEntity={selectEntityTile}
                />
                <MigrationSetupTile
                    isExpanded={isMigrationExpanded}
                    onToggle={() => setIsMigrationExpanded((value) => !value)}
                >
                    <ExternalSystemGrid
                        onSelect={selectExternalSystem}
                        className={styles.externalSystemsEmbedded}
                    />
                </MigrationSetupTile>
            </div>
        </div>
    );
};
